// chat.service.ts
import { Injectable, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, map, tap } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { io, Socket } from 'socket.io-client';
export interface ChatMessage {
  id: number;
  message: string;
  createdBy: number;
  createdAt: Date;
  channelId: number;
}

export interface ChatChannel {
  id: number;
  entityId: number;
  entityType: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private apiUrl = '/api/pu';
  private socketUrl!: string;

  private currentUserSubject = new BehaviorSubject<any | null>(null);
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private currentChannelSubject = new BehaviorSubject<ChatChannel | null>(null);
  private messageSentSubject = new BehaviorSubject<boolean>(false);

  messages$ = this.messagesSubject.asObservable();
  currentChannel$ = this.currentChannelSubject.asObservable();
  currentUser$ = this.currentUserSubject.asObservable();
  messageSent$ = this.messageSentSubject.asObservable();

  activeSocket = false;

  private socket!: Socket;

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService
  ) {
    if (this.activeSocket) {
      this.socketUrl = 'https://localhost:9001/';
      console.log('Tentative de connexion à:', this.socketUrl);

      this.socket = io(this.socketUrl, {
        path: '/chats-socket/socket.io',
        transports: ['websocket'],
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 30000,
        autoConnect: false,
        auth: {
          token: this.authenticationService.getAccessToken(),
        },
      });

      this.socket.on('connect_error', (error) => {
        console.error('Erreur de connexion détaillée:', error);
        console.error('Message:', error.message);
      });

      this.setupSocketListeners();
      this.socket.connect();
    }
  }

  private setupSocketListeners() {
    if (this.activeSocket && this.socket) {
      this.socket.on('connect', () => {
        console.log('Connecté au serveur Socket.IO');
      });

      this.socket.on('connect_error', (error) => {
        console.error('Erreur de connexion Socket.IO:', error);
        console.error("Détails de l'erreur:", error.message);
        console.error('Stack trace:', error.stack);
        // Ajoutez plus de détails si disponibles
      });

      this.socket.on('newMessage', (message: ChatMessage) => {
        console.log('newMessage', message);

        if (message.channelId === this.currentChannelSubject.value?.id) {
          const currentMessages = this.messagesSubject.value;
          this.messagesSubject.next([...currentMessages, message]);
        }
      });
    }
  }

  joinChannel(channelId: number) {
    if (this.activeSocket && this.socket) {
      console.log('joinChannel', channelId);
      this.socket.emit('joinChannel', channelId);
    }
  }

  leaveChannel(channelId: number) {
    if (this.activeSocket && this.socket) {
      this.socket.emit('leaveChannel', channelId);
    }
  }

  updateChannel(entityType: string, entityId: number) {
    // Quitter le canal actuel si existe
    if (this.currentChannelSubject.value) {
      this.leaveChannel(this.currentChannelSubject.value.id);
    }

    // set current user
    let currentUser = this.authenticationService.getCurrentUser();
    this.currentUserSubject.next(currentUser);

    this.getOrCreateChannel(entityType, entityId).subscribe((channel) => {
      this.currentChannelSubject.next(channel);
      this.updateMessages(channel.id);

      // Rejoindre le nouveau canal
      this.joinChannel(channel.id);
    });
  }

  getMessages(channelId: number): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(
      `${this.apiUrl}/chats/channels/${channelId}/messages`
    );
  }

  sendMessage(
    message: string,
    userId: number,
    channelId: number
  ): Observable<ChatMessage> {
    this.messageSentSubject.next(true);
    return this.http
      .post<ChatMessage>(
        `${this.apiUrl}/chats/channels/${channelId}/messages`,
        { message: message, userId }
      )
      .pipe(
        tap(() => {
          this.messageSentSubject.next(false);
        })
      );
  }

  getUsers(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/chats/users`);
  }

  getOrCreateChannel(
    entityType: string,
    entityId: number
  ): Observable<ChatChannel> {
    return this.http.post<ChatChannel>(`${this.apiUrl}/chats/channels`, {
      entityType,
      entityId,
    });
  }

  updateMessages(channelId: number) {
    this.getMessages(channelId).subscribe((messages) => {
      this.messagesSubject.next(messages);
    });
  }
}
